import React, { useState } from 'react';
import { Edit2, Check, X, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { formatDate } from '@/lib/utils';

const EditableField = ({ 
  value, 
  onSave, 
  label, 
  entityId, 
  fieldName, 
  type = 'text',
  options = [],
  className = ''
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  
  const { user } = useAuth();
  const { toast } = useToast();

  const handleSave = async () => {
    if (tempValue === value) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 600));
      
      const formattedOldValue = type === 'date' ? formatDate(value) : value;
      const formattedNewValue = type === 'date' ? formatDate(tempValue) : tempValue;

      onSave(fieldName, tempValue, value);
      setIsEditing(false);
      
      toast({
        title: "Alteração salva",
        description: `${label || fieldName} atualizado com sucesso.`
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Erro ao salvar",
        description: "Tente novamente mais tarde.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const displayValue = () => {
    if (type === 'select') return options.find(o => o.value === value)?.label || value;
    if (type === 'date') return formatDate(value);
    return value;
  };

  if (isEditing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {type === 'select' ? (
          <select
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            className="bg-white border border-orange-300 text-black text-sm rounded-md px-2 py-1 focus:ring-2 focus:ring-orange-500 outline-none w-full shadow-sm"
            autoFocus
          >
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            className="bg-white border border-orange-300 text-black text-sm rounded-md px-2 py-1 w-full min-w-[120px] focus:ring-2 focus:ring-orange-500 outline-none shadow-sm"
            autoFocus
          />
        )}
        <Button size="sm" variant="ghost" onClick={handleSave} disabled={isLoading} className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-100 shrink-0">
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </Button>
        <Button size="sm" variant="ghost" onClick={handleCancel} disabled={isLoading} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-100 shrink-0">
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className={`group relative flex items-center gap-2 ${className}`}>
      <div 
        onClick={() => setIsEditing(true)}
        className="cursor-pointer hover:bg-orange-50 hover:text-orange-900 px-1.5 py-0.5 rounded transition-colors flex items-center gap-2 min-w-0 border border-transparent hover:border-orange-200"
      >
        <span className="truncate">{displayValue()}</span>
        <Edit2 className="w-3 h-3 text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
      </div>
    </div>
  );
};

export default EditableField;